/**
 * WEB half of `PressableSurface` — the chrome sits one level in, on a `<View>` that becomes a
 * `<div>`, which no `button` / `[type=…]` / `input` selector can name. The `<button>` keeps the
 * semantics, the focus ring, the press handling and any LAYOUT the caller gives it.
 *
 * `./shared` has the defect this exists for, and why the native variant must not do the same.
 */
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { resolvePressStyle } from "./shared";
import type { PressableSurfaceProps } from "./shared";

export * from "./shared";

export function PressableSurface({ surface, style, children, ...rest }: PressableSurfaceProps) {
  return (
    <Pressable {...rest} style={style}>
      {(state) => (
        <View
          style={[
            // Fill the button, so a surface with no size of its own still covers the whole
            // control the caller laid out — see `styles.fill`.
            styles.fill,
            resolvePressStyle(surface, state),
          ]}
        >
          {/*
            `Pressable` passes a render function its state; a plain node child is returned as-is.
            Both forms are supported so a caller can keep whichever it already had.
          */}
          {typeof children === "function" ? (children as any)(state) : children}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /**
   * Fill the button on BOTH axes.
   *
   * `alignSelf: "stretch"` is the cross axis (width). The two growth properties are the main
   * axis, and they are spelled out rather than written `flex: 1` on purpose: **React Native's
   * `flex: 1` means `flexBasis: 0`**, which would collapse this View to nothing inside a button
   * that has no height of its own — which is most of them. `flexBasis: "auto"` keeps the
   * surface's own content height as its starting size, so it grows only into height a caller
   * actually gave the button.
   *
   * ⚠️ This matters as soon as a control's height lives on the PRESSABLE rather than here —
   * `KioskFooter` sizes its buttons that way. Without it the chrome would draw at content
   * height inside a taller button, leaving the fill visibly short of the control's edges.
   *
   * 🔴 **This file is the only place that growth is safe.** A browser sizes an auto-height flex
   * container to its content however its children grow; React Native's Yoga, under the
   * `StretchFlexBasis` errata RN sets, does the opposite and inflates the button to every point
   * its parent allows. That is why `index.native.tsx` has no inner View at all — see `./shared`.
   */
  fill: { alignSelf: "stretch", flexGrow: 1, flexShrink: 1, flexBasis: "auto" },
});

export default PressableSurface;
